// dependencies
import React from "react";
import { useIntl } from "react-intl";
import { StyleSheet } from "aphrodite";

// components
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid2";
import BaseComponent from "@cloudspire/legacy-resources/src/components/BaseComponent2";
import Text from "@cloudspire/legacy-resources/src/components/Text2";
import Heading from "@cloudspire/legacy-resources/src/components/Heading2";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import * as IconList from "@cloudspire/legacy-svg-icon-library";

// hocs
import useViewportMedia from "@cloudspire/legacy-resources/src/hocs/useViewportMedia";

// utils
import intlFormatConstantByValue from "@cloudspire/shared/utils/intl/formatConstantByValue";

// constants
import { ROOM_FEATURE_TYPE_INTL_MESSAGES } from "@cloudspire/shared/constants/room/feature";

/**
 * Récupère la liste des features d’hébergement par type en ne conservant que les features qui sont marquées comme "visible sur le site internet".
 */
function getRoomFeatureListByType({ featureListByType }) {
  const roomFeatureListByType = {};
  for (const [featureType, featureList] of Object.entries(featureListByType)) {
    for (const feature of featureList) {
      if (feature.searchable) {
        if (!roomFeatureListByType[featureType]) {
          roomFeatureListByType[featureType] = [];
        }
        roomFeatureListByType[featureType].push(feature);
      }
    }
  }
  return roomFeatureListByType;
}

const styles = StyleSheet.create({
  grid: {
    width: "100%",
  },
  heading: {
    fontWeight: 500,
    fontSize: "1.5rem",
  },
  featureCol: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
  },
  featureCol__odd: {
    backgroundColor: "#f7f7f7",
  },
});

type PropsType = {
  featureListByType: [string, { icon: string; id: string; name: string }[]][];
};

const RoomFeatureList: React.FunctionComponent<PropsType> = (props) => {
  const { featureListByType } = props;

  const intl = useIntl();

  const { matches } = useViewportMedia({ queries: [{ minWidth: 700 }] });

  const roomFeatureListByType = getRoomFeatureListByType({ featureListByType });

  return (
    <BaseComponent>
      <Row aStyle={styles.grid} gutter={1} type="column">
        {Object.entries(roomFeatureListByType).map(
          ([featureType, featureList], index) => {
            return (
              <Col
                key={featureType}
                aStyle={[
                  styles.featureCol,
                  index % 2 === 0 && styles.featureCol__odd,
                ]}
              >
                <Heading level={3} aStyle={styles.heading}>
                  {intlFormatConstantByValue(
                    {
                      intl,
                      messages: ROOM_FEATURE_TYPE_INTL_MESSAGES.byValue,
                      preferredAttributes: ["frontLabel", "label"],
                      value: featureType,
                    },
                    {
                      uppercaseFirst: true,
                    }
                  )}
                </Heading>
                <Stack marginTop={1} />
                <Row type="cell" gutter={0.3125} maxColumns={matches ? 3 : 1}>
                  {featureList.map((feature) => {
                    const hasIcon = feature.icon && IconList[feature.icon];

                    const Icon = hasIcon ? IconList[feature.icon] : () => null;

                    return (
                      <Col key={feature.id}>
                        <Row gutter={0.25} type="inline">
                          {hasIcon && (
                            <Col>
                              <Icon />
                            </Col>
                          )}

                          <Col
                            style={{ flexGrow: 1, flexShrink: 1 }}
                            allowOverflow={true}
                          >
                            <Text>
                              {feature.name}
                              {!isNaN(parseFloat(feature.distance))
                                ? ` ${intl.formatMessage(
                                    {
                                      defaultMessage:
                                        "{featureDistance, plural, =0 {sur place} other {à # km}}",
                                    },
                                    {
                                      featureDistance: parseFloat(
                                        feature.distance
                                      ),
                                    }
                                  )}`
                                : ""}
                            </Text>
                          </Col>
                        </Row>
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            );
          }
        )}
      </Row>
    </BaseComponent>
  );
};

export default RoomFeatureList;
